import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Checkbox, FormGroup, Textarea } from "@cofano/cck";
import Grid from "@mui/material/Grid2";

import { FormDialog } from "components/dialogs/Dialogs";

import { updateConnectingVisitNotesSchema, UpdateConnectingVisitNotesSchemaInput, UpdateConnectingVisitNotesSchemaOutput } from "view/connectingVisits/utils/connectingvisit.schema";

import { EditConnectingVisitUpdateTimeDocument, UpdateConnectingVisitNotesDocument } from "__generated__/view/connectingVisits/graphql/connectingVisit.mutations.generated";

const UpdateConnectingVisitNotesDialog = ({ open, onClose, actionId, connectingShipPortVisitId, initialNotes, refetch }: {
	open: boolean;
	onClose: () => void;
	actionId: number;
	connectingShipPortVisitId: number | null;
	initialNotes: string;
	refetch: () => void;
}) => {

	const { control, handleSubmit, reset } = useForm<UpdateConnectingVisitNotesSchemaInput, unknown, UpdateConnectingVisitNotesSchemaOutput>({
		resolver: zodResolver(updateConnectingVisitNotesSchema),
		defaultValues: {
			notes: "",
			updateLastContactTime: true,
		},
	});

	const handleOnClose = () => {
		onClose();
		reset({
			notes: "",
			updateLastContactTime: true,
		});
	};

	const [ updateVisitNote, { loading: updateVisitNoteLoading } ] = useMutation(UpdateConnectingVisitNotesDocument);
	const [ editLastContactTime, { loading: editLastContactTimeLoading } ] = useMutation(EditConnectingVisitUpdateTimeDocument);

	const loading = updateVisitNoteLoading || editLastContactTimeLoading;

	const save = async (data: UpdateConnectingVisitNotesSchemaOutput) => {
		updateVisitNote({
			variables: {
				id: actionId,
				notes: data.notes,
			},
		}).then(() => {
			if (data.updateLastContactTime && connectingShipPortVisitId) {
				editLastContactTime({
					variables: {
						id: connectingShipPortVisitId,
					},
				}).then(() => {
					refetch();
					handleOnClose();
				});
			} else {
				refetch();
				handleOnClose();
			}
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				notes: initialNotes || "",
				updateLastContactTime: true,
			});
		}
	}, [ open, initialNotes ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CONNECTING_VISIT_NOTES" })}
			form="updateConnectingVisitNotesForm"
			loading={loading}
			maxWidth="sm">
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="updateConnectingVisitNotesForm">
				<Grid container spacing={3}>
					<Grid size={12}>
						<FormGroup label={intl.formatMessage({ id: "CONNECTING_VISIT_NOTES" })} overflow="hidden">
							<Controller
								name="notes"
								control={control}
								render={({ field }) => (
									<Textarea
										{...field}
										style={{ minHeight: "100px" }}
										onFocus={(e) => {
											const length = e.target.value.length;
											e.target.setSelectionRange(length, length);
										}}
										autoFocus  />
								)}
							/>
						</FormGroup>
					</Grid>

					<Grid size={12}>
						<FormGroup label={intl.formatMessage({ id: "UPDATE_TERMINAL_LAST_CONTACT_TIME" })} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="updateLastContactTime"
								control={control}
								render={({ field }) => <Checkbox {...field} checked={field.value} value={undefined} />}
							/>
						</FormGroup>
					</Grid>
				</Grid>
			</form>
		</FormDialog>
	);
};

export default UpdateConnectingVisitNotesDialog;
